import { useState } from 'react';
import { useWhatsAppUnread } from '../hooks/useWhatsAppUnread';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../i18n';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Wallet,
  ArrowUpDown,
  History,
  CreditCard,
  RefreshCw,
  Settings,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Code,
  Users,
  FileText,
  QrCode,
  Store,
  Activity,
  Brain,
  Shield,
  Layers,
  Globe,
  Plane,
  Hotel,
  Landmark,
  Target,
  TrendingUp,
  Share2,
  UserCog,
  MessageCircle,
  Inbox
} from 'lucide-react';
import DeborahChat from './DeborahChat';
import { LanguageSwitcher } from './LanguageSwitcher';

// Navigation items with translation keys
const navItems = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { path: '/deposit', labelKey: 'nav.deposit', icon: Wallet },
  { path: '/transfer', labelKey: 'nav.transfer', icon: ArrowUpDown },
  { path: '/request', labelKey: 'nav.request', icon: QrCode },
  { path: '/affiliate', labelKey: 'nav.affiliate', icon: Share2 },
  { path: '/transactions', labelKey: 'nav.transactions', icon: History },
  { path: '/exchange', labelKey: 'nav.exchange', icon: RefreshCw },
  { path: '/forex-rates', labelKey: 'exchange.liveRates', icon: TrendingUp },
  { path: '/cards', labelKey: 'nav.cards', icon: CreditCard },
  { path: '/challenges', labelKey: 'nav.challenges', icon: Target },
  { path: '/insights', labelKey: 'nav.insights', icon: BarChart3 },
  { path: '/spending-insights', labelKey: 'nav.analytics', icon: Brain },
];

const moreNavItems = [
  { path: '/invoices', labelKey: 'nav.invoices', icon: FileText },
  { path: '/merchant', labelKey: 'nav.merchant', icon: Store },
  { path: '/partner', labelKey: 'nav.partner', icon: Layers },
  { path: '/team', labelKey: 'nav.team', icon: Users },
  { path: '/reports', labelKey: 'nav.reports', icon: BarChart3 },
  { path: '/developer', labelKey: 'nav.developer', icon: Code },
];

const globalHubItems = [
  { path: '/travel-directory', labelKey: 'nav.travel', icon: Plane, color: 'text-emerald-500' },
  { path: '/hotels-directory', labelKey: 'nav.hotels', icon: Hotel, color: 'text-blue-500' },
  { path: '/banks-directory', labelKey: 'nav.banks', icon: Landmark, color: 'text-amber-500' },
];

// Admin-only items
const adminNavItems = [
  { path: '/admin/referrals', label: 'Referral Management', icon: UserCog },
  { path: '/admin/whatsapp', label: 'WhatsApp Notifications', icon: MessageCircle },
  { path: '/admin/whatsapp/inbox', label: 'WhatsApp Inbox', icon: Inbox },
];

// Helper to check if user is admin
const isUserAdmin = (user) => {
  if (!user) return false;
  const adminEmails = ['admin@parachpay.com', 'support@parachpay.com', 'info@parachpay.com'];
  return (
    user.role === 'admin' ||
    user.is_admin === true ||
    adminEmails.includes(user.email?.toLowerCase())
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [globalHubOpen, setGlobalHubOpen] = useState(false);
  const whatsappUnread = useWhatsAppUnread(isUserAdmin(user));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="fixed left-4 top-4 bottom-4 w-64 hidden lg:flex flex-col rounded-2xl bg-card border border-border shadow-lg z-50">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="/images/parachpay-logo.jpg" 
              alt="ParachPay Logo" 
              width="40"
              height="40"
              className="h-10 w-auto"
              data-testid="sidebar-logo"
            />
            <span className="font-bold text-xl tracking-wide">PARACHPAY</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                  isActive 
                    ? 'bg-emerald-950 text-emerald-400' 
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-400' : ''}`} />
                <span className="font-medium">{t(item.labelKey)}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
          
          {/* More Section */}
          <div className="pt-4 mt-4 border-t border-border">
            <p className="px-4 py-2 text-xs text-muted-foreground font-semibold uppercase tracking-wide">{t('common.more')}</p>
            {moreNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                    isActive 
                      ? 'bg-emerald-950 text-emerald-400' 
                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-400' : ''}`} />
                  <span className="font-medium">{t(item.labelKey)}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
            
            {/* Global Business Hub - Expandable */}
            <div className="mt-1">
              <button
                onClick={() => setGlobalHubOpen(!globalHubOpen)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  globalHubOpen || globalHubItems.some(item => location.pathname === item.path)
                    ? 'bg-emerald-950/50 text-emerald-400' 
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
                data-testid="global-hub-toggle"
              >
                <Globe className={`w-5 h-5 ${globalHubOpen ? 'text-emerald-400' : ''}`} />
                <span className="font-medium flex-1 text-left">{t('nav.globalHub')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${globalHubOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Sub-items */}
              {globalHubOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-4">
                  {globalHubItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                          isActive 
                            ? 'bg-emerald-950 text-emerald-400' 
                            : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`hub-link-${item.path.replace('/', '')}`}
                      >
                        <Icon className={`w-4 h-4 ${isActive ? 'text-emerald-400' : item.color}`} />
                        <span className="text-sm font-medium">{t(item.labelKey)}</span>
                        {isActive && <ChevronRight className="w-3 h-3 ml-auto" />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Admin Section - Only visible to admins */}
            {isUserAdmin(user) && (
              <div className="pt-4 mt-4 border-t border-border">
                <p className="px-4 py-2 text-xs text-red-500 font-semibold uppercase tracking-wide flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Admin
                </p>
                {adminNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                        isActive 
                          ? 'bg-red-950 text-red-400' 
                          : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`admin-nav-${item.path.replace('/admin/', '').replace(/\//g, '-')}`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? 'text-red-400' : ''}`} />
                      <span className="font-medium">{item.label}</span>
                      {item.path === '/admin/whatsapp/inbox' && whatsappUnread > 0 && (
                        <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-green-600 text-white text-[11px] font-semibold flex items-center justify-center" data-testid="admin-nav-whatsapp-inbox-unread">
                          {whatsappUnread > 99 ? '99+' : whatsappUnread}
                        </span>
                      )}
                      {isActive && <ChevronRight className={`w-4 h-4 ${item.path === '/admin/whatsapp/inbox' && whatsappUnread > 0 ? '' : 'ml-auto'}`} />}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-border space-y-3">
          {/* Language Switcher */}
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-sm text-muted-foreground">{t('settings.language')}</span>
            <LanguageSwitcher variant="icon" />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors" data-testid="user-menu-trigger">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-emerald-950 text-emerald-400 font-semibold">
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="font-medium text-sm truncate">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                <Settings className="w-4 h-4 mr-2" />
                {t('nav.settings')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/sessions')} data-testid="sessions-menu-item">
                <Shield className="w-4 h-4 mr-2" />
                {t('nav.sessions')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle-menu-item">
                {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {theme === 'dark' ? t('settings.lightMode') : t('settings.darkMode')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                <LogOut className="w-4 h-4 mr-2" />
                {t('auth.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 z-50">
        <Link to="/dashboard" className="flex items-center">
          <img 
            src="/images/parachpay-logo.jpg" 
            alt="ParachPay Logo" 
            width="32"
            height="32"
            className="h-8 w-auto"
            data-testid="mobile-logo"
          />
        </Link>
        <div className="flex items-center gap-1">
          <LanguageSwitcher variant="icon" />
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="mobile-theme-toggle">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-background z-40 animate-fade-in overflow-y-auto">
          <nav className="p-4 space-y-2 pb-24">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-emerald-950 text-emerald-400' 
                      : 'hover:bg-muted'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{t(item.labelKey)}</span>
                </Link>
              );
            })}
            <div className="border-t border-border my-4" />
            <p className="px-4 py-2 text-xs text-muted-foreground font-semibold uppercase tracking-wide">{t('common.more')}</p>
            {moreNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-emerald-950 text-emerald-400' 
                      : 'hover:bg-muted'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{t(item.labelKey)}</span>
                </Link>
              );
            })}
            <div className="border-t border-border my-4" />
            <Link
              to="/settings"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-muted"
            >
              <Settings className="w-5 h-5" />
              <span className="font-medium">{t('nav.settings')}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-destructive/10 text-destructive"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">{t('auth.logout')}</span>
            </button>
          </nav>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 safe-area-bottom">
        <div className="flex items-center justify-around h-16">
          {navItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 min-w-0 h-full px-0.5 transition-colors ${
                  isActive 
                    ? 'text-emerald-500' 
                    : 'text-muted-foreground'
                }`}
                data-testid={`mobile-nav-${item.path.replace('/', '')}`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="block w-full text-[10px] leading-tight mt-1 font-medium text-center truncate">{t(`navShort.${item.labelKey.replace('nav.', '')}`, { defaultValue: t(item.labelKey).split(' ')[0] })}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-16 pb-20 lg:pt-0 lg:pb-0">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
      
      {/* Deborah Chat Widget */}
      <DeborahChat />
    </div>
  );
};
